import React from 'react';
import styled, { css } from 'styled-components';
import { Reference } from '@sanity/types';

import { urlForImage } from 'utils/sanity';
import { ImageContainer as ArticleImageProps } from 'lib/sanity/types';

// To support the retina devices, size of the images are always doubled

// 564 is the max width of the images
const IMAGE_MAX_WIDTH = 564;
// Using iPhone 8 Plus as the break point for phones
const IPHONE_PLUS_WIDTH = 414;

interface Props {
  image?: ArticleImageProps;
  inline?: boolean;
}

const Img = styled.img<Props>`
  max-width: 100%;
  vertical-align: middle;
  ${props =>
    props.inline &&
    css`
      height: 30px;
    `}
`;

const urlForAsset = (asset: Reference) => (maxWidth: number) => urlForImage(asset, maxWidth);

const ArticleImage = ({ image, inline = false }: Props) => {
  if (!image?.image?.asset) {
    return null;
  }

  const {
    alt,
    image: { asset },
  } = image;

  const getAssetUrlWithMaxWidth = urlForAsset(asset);

  if (inline) return <Img src={getAssetUrlWithMaxWidth(60)} alt={alt} inline={inline} />;

  return (
    <picture>
      {/* `*2` is for retina screens */}
      {/* Screens that are smaller than iPhone 8 Plus will load the same */}
      <source
        media="(max-width: 414px)"
        srcSet={`${getAssetUrlWithMaxWidth(IPHONE_PLUS_WIDTH)} 1x, ${getAssetUrlWithMaxWidth(
          IPHONE_PLUS_WIDTH * 2,
        )} 2x`}
      />
      {/* ... bigger than iPhone 8 Plus will load the max */}
      <source
        media="(min-width: 415px)"
        srcSet={`${getAssetUrlWithMaxWidth(IMAGE_MAX_WIDTH)} 1x, ${getAssetUrlWithMaxWidth(IMAGE_MAX_WIDTH * 2)} 2x`}
      />
      <Img src={getAssetUrlWithMaxWidth(IMAGE_MAX_WIDTH * 2)} alt={alt} inline={inline} />
    </picture>
  );
};

export default ArticleImage;
